import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_Overflow = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">🌀 CSS Overflow</h1>

      <p className="tutorial-intro">
        The <code>overflow</code> property in CSS specifies what happens when
        content overflows an element’s box. It controls whether the overflowing
        content is visible, hidden, scrollable, or adjusted automatically.
      </p>

      {/* Why Use Overflow */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
          textAlign: "center",
        }}
      >
        <h2 style={{ color: "#075985", marginBottom: "0.5rem" }}>
          🌟 Why Use CSS Overflow?
        </h2>
        <p style={{ fontSize: "1.1rem", lineHeight: "1.6" }}>
          Positioning is essential when you want to control large content inside
          small containers. Overflow helps in creating scroll boxes, hiding
          extra text, or designing clean layouts without breaking the design.
        </p>
      </div>

      {/* Overflow: visible */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ overflow: visible (default)</h2>
        <p>
          The content is not clipped and will overflow outside the container.
        </p>

        <CodeCopyBlock
          code={`.box {
  width: 200px;
  height: 100px;
  border: 2px solid #0284c7;
  overflow: visible;
}`}
        />

        <div
          style={{
            width: "200px",
            height: "100px",
            border: "2px solid #0284c7",
            marginTop: "1rem",
            overflow: "visible",
            padding: "5px",
          }}
        >
          This text is longer than the box and will overflow outside.
        </div>
      </div>

      {/* Overflow: hidden */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ overflow: hidden</h2>
        <p>The content is clipped and hidden outside the container.</p>

        <CodeCopyBlock
          code={`.box {
  width: 200px;
  height: 100px;
  border: 2px solid #dc2626;
  overflow: hidden;
}`}
        />

        <div
          style={{
            width: "200px",
            height: "100px",
            border: "2px solid #dc2626",
            marginTop: "1rem",
            overflow: "hidden",
            padding: "5px",
          }}
        >
          This text is longer than the box but will be hidden instead of showing
          outside.
        </div>
      </div>

      {/* Overflow: scroll */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ overflow: scroll</h2>
        <p>
          The content is clipped, but scrollbars always appear (horizontal &
          vertical).
        </p>

        <CodeCopyBlock
          code={`.box {
  width: 200px;
  height: 100px;
  border: 2px solid #16a34a;
  overflow: scroll;
}`}
        />

        <div
          style={{
            width: "200px",
            height: "100px",
            border: "2px solid #16a34a",
            marginTop: "1rem",
            overflow: "scroll",
            padding: "5px",
          }}
        >
          This text is very long and will create scrollbars even if the content
          fits in some cases.
        </div>
      </div>

      {/* Overflow: auto */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ overflow: auto</h2>
        <p>
          The content is clipped, but scrollbars appear only if necessary
          (better for responsive design).
        </p>

        <CodeCopyBlock
          code={`.box {
  width: 200px;
  height: 100px;
  border: 2px solid #9333ea;
  overflow: auto;
}`}
        />

        <div
          style={{
            width: "200px",
            height: "100px",
            border: "2px solid #9333ea",
            marginTop: "1rem",
            overflow: "auto",
            padding: "5px",
          }}
        >
          This text will create scrollbars only when needed, not always like
          <code>scroll</code>.
        </div>
      </div>

      {/* Horizontal & Vertical Overflow */}
      <div className="tutorial-section">
        <h2 className="section-title">5️⃣ Horizontal & Vertical Overflow</h2>
        <p>
          You can control <code>overflow-x</code> and <code>overflow-y</code>{" "}
          separately for horizontal and vertical scrolling.
        </p>

        <CodeCopyBlock
          code={`.box-x {
  overflow-x: scroll;
  overflow-y: hidden;
}

.box-y {
  overflow-y: scroll;
  overflow-x: hidden;
}`}
        />

        <div
          style={{
            display: "flex",
            gap: "20px",
            marginTop: "1rem",
          }}
        >
          <div
            style={{
              width: "150px",
              height: "80px",
              border: "2px solid #f59e0b",
              overflowX: "scroll",
              overflowY: "hidden",
              padding: "5px",
              whiteSpace: "nowrap",
            }}
          >
            This is a horizontal scrolling example with overflow-x set to scroll
            only.
          </div>

          <div
            style={{
              width: "150px",
              height: "80px",
              border: "2px solid #2563eb",
              overflowY: "scroll",
              overflowX: "hidden",
              padding: "5px",
            }}
          >
            This is a vertical scrolling example with overflow-y set to scroll
            only. Add more lines... <br />
            Line 2 <br />
            Line 3 <br />
            Line 4
          </div>
        </div>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          Now that you know how to use <code>overflow</code>, try combining it
          with{" "}
          <a href="/css/position" className="learn-more">
            CSS Position
          </a>{" "}
          and{" "}
          <a href="/css/zindex" className="learn-more">
            CSS Z-Index
          </a>{" "}
          for advanced layouts like modals, dropdowns, and scrollable content
          boxes.
        </p>
      </div>
    </div>
  );
};

export default CSS_Overflow;
